import React, { useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import CaretRight from '@/components/Icons/CaretRight';
import CheckCircle from '@/components/Icons/CheckCircle';
import CreditReportIcon from '@/components/Icons/CreditReport';
import TaskEfficiencyIndicator from '@/components/TaskEfficiencyIndicator';
import * as format from '@/format';
import useNoodleApi from '@/hooks/useNoodleApi';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import CreditReportModal from '@/modals/CreditReportModal';
import CustomCreditReportApprovalModal from '@/modals/CreditReportModal/CustomCreditReportApproval';
import SkipTaskConfirmationModal from '@/modals/SkipTaskConfirmationModal';
import { useOpenCreditReportPane } from '@/panes/hooks';
import { useUser } from '@/providers/Auth';
import * as tsClient from '@/tsClient';
import { type Creator, type CreditReport, MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@/typings/api-models';

import s from './CreditReportCard.module.scss';

type Props = {
  creator: Pick<Creator, 'slug'>;
  request: Pick<CreditReport, 'id' | 'completedAt' | 'skippedAt' | 'status'> & {
    responses: Array<{
      creditReportResponseId: string;
      personId: string;
      needsCreatorAction: boolean;
      isCompleted: boolean;
    }>;
  };
  canClick: boolean;
  canSkip: boolean;
  setIsFulfilled?: (b: boolean) => void;
  setIsSkipped?: (b: boolean) => void;
  onChange?: () => Promise<void>;
  completedAt?: string | null;
  integrationSlugs?: string[];
  userWorkflowId?: string;
  assigneeIds?: string[];
};

const CreditReportCard: React.FC<Props> = ({
  creator,
  userWorkflowId,
  setIsFulfilled,
  setIsSkipped,
  canClick,
  canSkip,
  request,
  assigneeIds = [],
  onChange,
}) => {
  const [user] = useUser();
  const [skipTaskModalOpen, setSkipTaskModalOpen] = useState(false);
  const [isCreditReportModalOpen, setCreditReportModalOpen] = useState(false);
  const [isCreditReportApprovalModalOpen, setCreditReportApprovalModalOpen] = useState(false);

  const { isInDashboard } = useContext(DashboardContext);
  const openCreditReportPane = useOpenCreditReportPane();

  const { getData: updateCreditReport } = useNoodleApi(tsClient.creditReportsB2C.updateCreditReport, {
    toastOnError: true,
  });
  const { data: creatorData, getData: getCreatorData } = useNoodleApi(tsClient.getCreator, {
    toastOnError: true,
  });

  const response = request.responses?.find(({ personId }) => personId === user?.id);
  const isCompleted = !!request.completedAt || !!response?.isCompleted;
  const isSkipped = !!request.skippedAt;

  useEffect(() => {
    setIsFulfilled?.(isCompleted);
  }, [isCompleted, setIsFulfilled]);

  useEffect(() => {
    setIsSkipped?.(isSkipped);
  }, [isSkipped, setIsSkipped]);

  useEffect(() => {
    if (creator.slug) {
      getCreatorData({ creatorSlug: creator.slug });
    }
  }, [creator.slug]);

  const handleClick = (): void => {
    /* Open pane if;
     * - credit report is completed
     * - the user is one of debtors and he already purchased the credit report
     * - the user is attorney (credit report has responses but none for current user)
     */
    if (
      request.status === 'completed' ||
      response?.isCompleted ||
      (request.responses.some((r) => !r.needsCreatorAction) && !response)
    ) {
      openCreditReportPane({ creditReportId: request.id });
    } else if (!isSkipped) {
      if (request.status === 'approval-request-sent') {
        // allow only assignees can proceed purchasing flow
        if (assigneeIds.includes(user?.id ?? '')) {
          setCreditReportApprovalModalOpen(true);
        }
      } else {
        setCreditReportModalOpen(true);
      }
    }
  };

  const handleSkipTask = async (): Promise<void> => {
    await updateCreditReport({
      creditReportId: request.id,
      status: 'skipped',
    });
    setSkipTaskModalOpen(false);
    if (onChange) onChange();
  };

  const handleUnskipTask = async (): Promise<void> => {
    await updateCreditReport({
      creditReportId: request.id,
      skippedAt: null,
    });
    if (onChange) onChange();
  };

  const handleConfirmTask = async (): Promise<void> => {
    setCreditReportModalOpen(false);
    setCreditReportApprovalModalOpen(false);
    if (onChange) onChange();
  };

  const closeModal = (): void => {
    setCreditReportModalOpen(false);
  };

  return (
    <>
      {skipTaskModalOpen && (
        <SkipTaskConfirmationModal
          onClose={() => setSkipTaskModalOpen(false)}
          onConfirm={handleSkipTask}
          taskType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT}
        />
      )}

      {isCreditReportModalOpen && (
        <CreditReportModal
          creator={creatorData}
          creditReportId={request.id}
          userWorkflowId={userWorkflowId}
          onConfirm={handleConfirmTask}
          onClose={() => closeModal()}
        />
      )}

      {isCreditReportApprovalModalOpen && (
        <CustomCreditReportApprovalModal
          creditReportId={request.id}
          onConfirm={handleConfirmTask}
          onClose={() => setCreditReportApprovalModalOpen(false)}
        />
      )}

      <Buttons
        key={request.id}
        className={s.container}
        isWrapper
        isFullWidth
        disabled={!canClick || (isSkipped && !canSkip)}
        style={isSkipped ? { backgroundColor: 'var(--color-gray-25)', borderRadius: 8 } : {}}
        onClick={handleClick}
      >
        {isCompleted ? (
          <CheckCircle size={16} weight='fill' color='var(--color-success)' />
        ) : (
          <figure>
            {isSkipped && <CreditReportIcon size={20} color='var(--color-gray-100)' />}
            {!isSkipped && <CreditReportIcon size={20} color='var(--color-primary)' />}
          </figure>
        )}
        <div>
          <div className={s.type}>
            <p
              className={isCompleted ? 'caption-bold' : 'body-sm-bold'}
              style={isCompleted ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
            >
              Get Credit Report
            </p>
          </div>
          {isCompleted && request.completedAt && (
            <p className='caption'>
              Completed on {format.datetimeLocalTZ.withoutTimezone({ datetime: request.completedAt })}
            </p>
          )}
          {isSkipped && <p className='caption'>This task is no longer required.</p>}
        </div>
        {canClick && !isSkipped && (
          <>
            {isCompleted ? (
              <CaretRight size={12} color='var(--color-gray-100)' />
            ) : (
              <div
                className={s.open}
                {...(!isCompleted &&
                  !isInDashboard && {
                    style: {
                      backgroundColor: 'var(--color-primary)',
                      border: '1px solid var(--color-primary)',
                      color: 'var(--color-gray-0)',
                    },
                  })}
              >
                View
              </div>
            )}
          </>
        )}
        {isSkipped && isInDashboard && (
          <Buttons
            isWrapper
            onClick={(e) => {
              e.stopPropagation();
              handleUnskipTask();
            }}
          >
            <div className={s.open}>Unskip</div>
          </Buttons>
        )}
      </Buttons>
      <TaskEfficiencyIndicator
        className='px-3 py-1'
        referenceType='credit_report'
        referenceId={request.id}
        isSkipped={isSkipped}
      />
      {canSkip && !isSkipped && (
        <Buttons
          onClick={(event) => {
            event.stopPropagation();
            setSkipTaskModalOpen(true);
          }}
          className={s.skip}
        >
          Skip
        </Buttons>
      )}
    </>
  );
};

export default CreditReportCard;
